import React from 'react';
import styled from 'styled-components';
import { uiTokens } from './tokens';

type HeadingLevel = 1 | 2 | 3 | 4;
type HeadingSize = 'xl' | 'lg' | 'md' | 'sm';

interface HeadingProps {
  level?: HeadingLevel;
  size?: HeadingSize;
  align?: 'left' | 'center' | 'right';
  accentText?: string;
  italicAccent?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const sizeMap: Record<HeadingSize, string> = {
  xl: 'clamp(2rem, 4.5vw, 3rem)',
  lg: 'clamp(1.55rem, 3vw, 2.1rem)',
  md: 'clamp(1.2rem, 2.2vw, 1.55rem)',
  sm: '1.05rem',
};

const StyledHeading = styled.h1<{ $size: HeadingSize; $align: 'left' | 'center' | 'right' }>`
  margin: 0;
  font-size: ${({ $size }) => sizeMap[$size]};
  font-weight: 400;
  line-height: 1.2;
  color: ${uiTokens.colors.textPrimary};
  text-align: ${({ $align }) => $align};
  font-family: ${uiTokens.typography.serif};
`;

const Accent = styled.em<{ $italic: boolean }>`
  color: ${uiTokens.colors.accent};
  font-style: ${({ $italic }) => ($italic ? 'italic' : 'normal')};
`;

export const Heading: React.FC<HeadingProps> = ({
  level = 1,
  size = 'xl',
  align = 'left',
  accentText,
  italicAccent = true,
  className,
  style,
  children,
}) => {
  const tag = `h${level}` as keyof JSX.IntrinsicElements;
  return (
    <StyledHeading as={tag} $size={size} $align={align} className={className} style={style}>
      {children}
      {accentText ? (
        <>
          {' '}
          <Accent $italic={italicAccent}>{accentText}</Accent>
        </>
      ) : null}
    </StyledHeading>
  );
};

export const Kicker = styled.p<{ align?: 'left' | 'center' | 'right' }>`
  margin: 0 0 0.5rem;
  text-align: ${({ align = 'left' }) => align};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${uiTokens.colors.accentHover};
  font-family: ${uiTokens.typography.sans};
`;

export const Subheading = styled.p<{ align?: 'left' | 'center' | 'right' }>`
  margin: 0;
  text-align: ${({ align = 'left' }) => align};
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.55;
  color: ${uiTokens.colors.textSecondary};
  font-family: ${uiTokens.typography.sans};
`;

export default Heading;
